import {
  cn,
  Flex,
  LabelField,
  LabelFieldError,
  LabelFieldMain,
  LabelFieldSupporting,
  Radio,
  RadioProps,
} from '@bridebook/aura';
import { ElementRef, forwardRef, PropsWithChildren, ReactNode, useId, useMemo } from 'react';
import { useRadioGroupContext } from '../radio-group/radio-group-context';

export type RadioFieldSize = 'standard' | 'sm';

type RadioFieldProps = Omit<RadioProps, 'error'> & {
  label: ReactNode;
  description?: ReactNode;
  error?: boolean | string;
  size?: RadioFieldSize;
  classNames?: {
    wrapper?: string;
    labelWrapper?: string;
    labelMain?: string;
    labelSupporting?: string;
    labelError?: string;
  };
};

export const RadioField = forwardRef<ElementRef<typeof Radio>, RadioFieldProps>(
  (
    {
      label,
      description,
      id: idProps,
      disabled = false,
      error: errorProp = false,
      size: sizeProp,
      classNames,
      ...props
    },
    ref,
  ) => {
    const { error: groupError, size: groupSize } = useRadioGroupContext();

    const error = errorProp || groupError;
    const size = sizeProp || groupSize;

    const defaultId = useId();
    const id = idProps ?? defaultId;

    const fieldId = `${id}-field`;
    const errorId = `${id}-error`;
    const supportingId = `${id}-supporting`;

    const hasErrorMessage = typeof errorProp === 'string';

    const ariaDescribedby = useMemo(
      () =>
        [description ? supportingId : undefined, hasErrorMessage ? errorId : undefined]
          .filter(Boolean)
          .join(' ') || undefined,
      [description, supportingId, hasErrorMessage, errorId],
    );

    return (
      <Flex className={cn('gap-2', classNames?.wrapper)}>
        <SpacingWrapper size={size}>
          <Radio
            error={!!error}
            disabled={disabled}
            id={fieldId}
            ref={ref}
            aria-describedby={ariaDescribedby}
            {...props}
          />
        </SpacingWrapper>
        <LabelField
          size={size}
          disabled={disabled}
          fieldId={fieldId}
          supportingId={supportingId}
          errorId={errorId}
          className={classNames?.labelWrapper}>
          <LabelFieldMain className={classNames?.labelMain}>{label}</LabelFieldMain>
          {description && (
            <LabelFieldSupporting className={classNames?.labelSupporting}>
              {description}
            </LabelFieldSupporting>
          )}
          {hasErrorMessage && (
            <LabelFieldError className={classNames?.labelError}>{errorProp}</LabelFieldError>
          )}
        </LabelField>
      </Flex>
    );
  },
);
RadioField.displayName = 'RadioField';

// Wrapper used for small radio button.
// Needed to align the radio button with the label,
// since the labels title line height is 20px.
const SpacingWrapper = ({
  children,
  size,
}: PropsWithChildren<{ size: RadioFieldProps['size'] }>) => {
  if (size === 'sm') {
    return <div className="flex h-5 w-6 items-center justify-center">{children}</div>;
  }
  return <>{children}</>;
};
